"use no memo";

import type {
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import type { ReactNode } from "react";
import React from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { formatIntToCurrency } from "@repo/utils";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ArrowDown, FilterIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ColumnDefWithTotal } from "~/types";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { cn } from "~/lib/utils";
import { isColumnDefWithTotal } from "~/types";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { DataTablePagination } from "./table-pagination";

interface DataTableProps<TData, TValue> {
  columns: ColumnDefWithTotal<TData, TValue>[] | ColumnDef<TData, TValue>[];
  data: TData[];
  onSelect?: (row: TData) => void;
  title?: string;
  rowLimit?: number;
  footerContent?: ReactNode;
  emptyPlaceHolder?: string;
  filterActiveForColumns?: string[];
  pdfExport?: {
    filename: string;
    enabled?: boolean;
    title?: string;
    description?: string;
    onExport?: () => void;
  };
  borders?: boolean;
  csvExport?: {
    filename: string;
    enabled?: boolean;
  };
  showPagination?: boolean;
  showHeader?: boolean;
  defaultSorting?: SortingState;
  className?: string;
  buttons?: React.ReactNode[];
  ref?: React.RefObject<HTMLDivElement>;
}

const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
  const rawValue = row.getValue(columnId);

  // Ensure rawValue is a valid string or convert it to a string representation
  const stringValue =
    typeof rawValue === "string"
      ? rawValue
      : rawValue != null
        ? String(rawValue)
        : ""; // Fallback to empty string if the value is null or undefined

  // Rank the item using the stringified value
  const itemRank = rankItem(stringValue, value as string);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  onSelect,
  pdfExport,
  csvExport,
  borders = false,
  showPagination = true,
  defaultSorting,
  showHeader = true,
  rowLimit = 10,
  className,
  emptyPlaceHolder,
  filterActiveForColumns,
  footerContent,
  ref,
  buttons,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>(
    defaultSorting ?? [],
  );

  const [filterOpen, setFilterOpen] = React.useState(false);

  const [dragRef] = useDragScroll();

  const { t } = useTranslation("common");

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const [rowSelection, setRowSelection] = React.useState({});

  const [globalFilter, setGlobalFilter] = React.useState("");

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowLimit,
  });

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
      exact: (row, columnId, value) => {
        const rowValue = row.getValue(columnId);
        return rowValue === value;
      },
    },
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: "auto",
    autoResetAll: false,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      pagination,
      rowSelection,
    },
  });

  const handlePdf = () => {
    if (!pdfExport || pdfExport.enabled === false) return;

    if (pdfExport.onExport) {
      pdfExport.onExport();
      return;
    }

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });
    doc.setFontSize(15);

    if (pdfExport.title) {
      doc.text(pdfExport.title, 40, 40);
    }

    const sortedFilteredRows = table.getSortedRowModel().rows;

    const totals = columns.map((column) => {
      if (!isColumnDefWithTotal(column)) return "";
      const total = sortedFilteredRows.reduce((sum, row) => {
        const cell = row
          .getAllCells()
          .find((cell) => cell.column.id === column.id);
        const value = cell ? cell.getValue() : null;
        if (typeof value === "number") {
          return sum + value;
        } else {
          return sum;
        }
      }, 0);
      return formatIntToCurrency(total, false);
    });

    autoTable(doc, {
      head: table
        .getHeaderGroups()
        .map((headerGroup) =>
          headerGroup.headers.map((header) => header.column.id),
        ),
      body: sortedFilteredRows.map((row) =>
        row.getAllCells().map((cell) => {
          const value = cell.getValue();
          if (value instanceof Date) {
            return format(value, "Pp");
          } else if (typeof value === "number") {
            return formatIntToCurrency(value, false);
          } else {
            return value ?? "";
          }
        }),
      ),
      foot: [totals],
      headStyles: { fillColor: [22, 160, 133] },
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255] },
      startY: 50,
      theme: "striped",
    });

    doc.save(`${pdfExport.filename}.pdf`);
  };

  const handleCSV = () => {
    if (!csvExport || csvExport.enabled === false) return;

    // Get sorted and filtered rows
    const sortedFilteredRows = table.getSortedRowModel().rows;

    // Get CSV headers
    const headers = table
      .getHeaderGroups()
      .map((headerGroup) =>
        headerGroup.headers.map((header) => header.column.id),
      );

    // Prepare CSV rows
    const rows = sortedFilteredRows.map((row) =>
      row.getAllCells().map((cell) => {
        const value = cell.getValue();
        if (value instanceof Date) {
          return format(value, "Pp"); // Format dates if necessary
        } else {
          return value;
        }
      }),
    );

    // Convert to CSV string
    const csvContent = [
      headers.join(","), // Join headers with commas
      ...rows.map((row) => row.join(",")), // Join each row's values with commas
    ].join("\n"); // Join all rows with newlines

    // Create a Blob and trigger the download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${csvExport.filename}.csv`); // Name of the CSV file
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // Clean up
  };

  return (
    <div className="flex flex-col gap-2 @container h-full overflow-auto">
      {showHeader !== false && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-between gap-2">
            <Input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={t("search")}
            />
            {!!globalFilter && (
              <Button
                onClick={() => {
                  setGlobalFilter("");
                }}
              >
                {t("clear")}
              </Button>
            )}
            {filterActiveForColumns && (
              <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <FilterIcon />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Filters</DialogTitle>
                    <VisuallyHidden>
                      <DialogDescription></DialogDescription>
                    </VisuallyHidden>
                  </DialogHeader>
                  {(() => {
                    const filter = filterActiveForColumns[0];
                    let column: Column<TData, unknown> | undefined;
                    let uniqueValues: string[] = [];

                    if (filter) {
                      column = table.getColumn(filter);
                      uniqueValues = column
                        ? (Array.from(
                            column.getFacetedUniqueValues().keys(),
                          ) as string[])
                        : [];
                    }

                    return (
                      <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
                        <div className="flex flex-col gap-2">
                          {uniqueValues
                            .sort((a, b) => a.localeCompare(b))
                            .map((value) => (
                              <Button
                                key={value}
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                  column?.setFilterValue(value);
                                  setFilterOpen(false);
                                }}
                              >
                                {value}
                              </Button>
                            ))}
                        </div>
                      </div>
                    );
                  })()}

                  <DialogFooter>
                    <Button
                      onClick={() => {
                        table.resetColumnFilters();
                        setFilterOpen(false);
                      }}
                      variant="warning"
                    >
                      Reset filter
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  {t("columns")} <ArrowDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => {
                          if (
                            table.getVisibleFlatColumns().length === 1 &&
                            value === false
                          ) {
                            return;
                          }
                          column.toggleVisibility(!!value);
                        }}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {buttons && <div className="flex flex-row gap-2">{buttons}</div>}
          {(pdfExport ?? csvExport) && (
            <div className="flex flex-row gap-2">
              {pdfExport?.enabled !== false && (
                <div className="flex flex-row gap-2">
                  <Button variant="outline" onClick={handlePdf}>
                    {t("save_as_pdf")}
                  </Button>
                </div>
              )}
              {csvExport?.enabled !== false && (
                <div className="flex flex-row gap-2">
                  <Button variant="outline" onClick={handleCSV}>
                    {t("save_as_csv")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div
        ref={(e) => {
          if (ref) {
            ref.current = e;
          }
          dragRef(e);
        }}
        className={cn(
          "rounded-md border overflow-auto hide-scrollbar h-full",
          className,
        )}
      >
        <Table className={cn(data.length === 0 && "h-full")}>
          {showHeader !== false && (
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead className="px-0" key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className="even:bg-muted"
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      className={cn(borders && "border-r last:border-r-0 p-2")}
                      key={cell.id}
                      onClick={() => {
                        if (
                          onSelect &&
                          cell.column.columnDef.meta?.canSelect !== false
                        ) {
                          console.log(cell);
                          onSelect(row.original);
                        }
                      }}
                      width={cell.column.columnDef.size}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyPlaceHolder ?? t("no_results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {footerContent && (
            <TableFooter className="sticky bottom-0 z-50 bg-secondary">
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length}>
                  {footerContent}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
      {showPagination && <DataTablePagination table={table} />}
    </div>
  );
}
